import type { WcLive, WcPlayer, WcTeam } from './types';

export class WcError extends Error {
  constructor(message: string, readonly status?: number) {
    super(message);
    this.name = 'WcError';
  }
}

/**
 * Reads the World Cup snapshot from the worker's `/wc/*` routes (which serve the
 * DO's stored ingest — NOT a live api-football call). The snapshot is fetched
 * once and memoized for the session, so building/editing a team never hits the
 * wire again. In-flight dedupe collapses concurrent loads.
 */
export class WcClient {
  private readonly base: string;
  private inflight = new Map<string, Promise<unknown>>();
  private playersMemo: WcPlayer[] | null = null;
  private teamsMemo: WcTeam[] | null = null;

  /** `base` is the worker origin with no trailing slash, e.g. `https://x`. */
  constructor(base: string) {
    this.base = base.replace(/\/$/, '');
  }

  async getPlayers(force = false): Promise<WcPlayer[]> {
    if (!force && this.playersMemo) return this.playersMemo;
    const data = await this.get<{ players: WcPlayer[] }>('/wc/players');
    this.playersMemo = data.players ?? [];
    return this.playersMemo;
  }

  async getTeams(force = false): Promise<WcTeam[]> {
    if (!force && this.teamsMemo) return this.teamsMemo;
    const data = await this.get<{ teams: WcTeam[] }>('/wc/teams');
    this.teamsMemo = data.teams ?? [];
    return this.teamsMemo;
  }

  /** Live game state (gameweeks, live player points, manager scores/transfers).
   *  Never memoised — always fetched fresh (cache-busted) so scores stay current. */
  async getLive(): Promise<WcLive> {
    return this.fetchJson<WcLive>(`${this.base}/wc/live?t=${Date.now()}`);
  }

  private get<T>(path: string): Promise<T> {
    const url = this.base + path;
    const existing = this.inflight.get(url);
    if (existing) return existing as Promise<T>;
    const promise = this.fetchJson<T>(url).finally(() => this.inflight.delete(url));
    this.inflight.set(url, promise);
    return promise;
  }

  private async fetchJson<T>(url: string): Promise<T> {
    let res: Response;
    try {
      res = await fetch(url, { headers: { accept: 'application/json' } });
    } catch (err) {
      throw new WcError(`Network error fetching ${url}: ${String(err)}`);
    }
    if (!res.ok) throw new WcError(`WC request failed (${res.status})`, res.status);
    return (await res.json()) as T;
  }
}
